import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PhysicalProductView from '../components/PhysicalProductView';
import ServiceProductView from '../components/ServiceProductView';
import ProductReviews from '../components/ProductReviews';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Star, MessageCircle, ChevronRight, Copy, Store } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { shippingAddress } = useCart();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState([]);

  // Resell Logic
  const [userStores, setUserStores] = useState([]);
  const [resellDialogOpen, setResellDialogOpen] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [republishing, setRepublishing] = useState(false);

  const fetchProduct = React.useCallback(async () => {
    try {
      let urlParams = "";
      const defaultAddr = shippingAddress || (user && user.saved_addresses ? (user.saved_addresses.find(a => a.id === user.default_address_id) || user.saved_addresses[0]) : null);
      if (defaultAddr && defaultAddr.coordinates) {
        const lat = defaultAddr.coordinates.lat;
        const lon = defaultAddr.coordinates.lon || defaultAddr.coordinates.lng;
        if (lat && lon) {
          urlParams = `?user_lat=${lat}&user_lon=${lon}`;
        }
      }

      const response = await axios.get(`${API_URL}/products/${productId}${urlParams}`);
      setProduct(response.data);
      setStore(response.data.store);

      // Fetch related products
      if (response.data.category_id) {
        const relatedUrl = `${API_URL}/products?category_id=${response.data.category_id}&limit=4` + (urlParams ? `&${urlParams.substring(1)}` : "");
        const relatedRes = await axios.get(relatedUrl);
        setRelatedProducts(relatedRes.data.filter(p => p.id !== productId));
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Produit non trouvé');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleRepublishClick = async () => {
    if (!isAuthenticated) {
      toast.error("Veuillez vous connecter pour vendre ce produit");
      navigate('/auth');
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/stores/my-stores`);
      const stores = res.data;
      if (stores.length === 0) {
        toast.error("Vous n'avez pas de boutique. Créez-en une d'abord.");
        return;
      }
      setUserStores(stores);
      if (stores.length === 1) {
        setSelectedStoreId(stores[0].id);
      }
      setResellDialogOpen(true);
    } catch (e) {
      toast.error("Erreur lors de la récupération de vos boutiques");
    }
  };

  const confirmRepublish = async () => {
    if (!selectedStoreId) return;
    setRepublishing(true);
    try {
      await axios.post(`${API_URL}/stores/${selectedStoreId}/republish/${productId}`);
      toast.success("Produit ajouté à votre boutique !");
      setResellDialogOpen(false);
      // Redirect to store dashboard to edit price?
      // navigate(`/boutique/${selectedStoreId}/dashboard`);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setRepublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Produit non trouvé</h1>
          <Link to="/catalogue" className="text-orange-600 hover:underline">
            Retour au catalogue
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-orange-600">Accueil</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/catalogue" className="hover:text-orange-600">Catalogue</Link>
            <ChevronRight className="h-4 w-4" />
            {product.category && (
              <>
                <Link
                  to={`/catalogue?category=${product.category.slug}`}
                  className="hover:text-orange-600"
                >
                  {product.category.name}
                </Link>
                <ChevronRight className="h-4 w-4" />
              </>
            )}
            <span className="text-gray-900 font-medium truncate max-w-xs">
              {product.title}
            </span>
          </div>
        </div>
      </div>



      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* ROUTER LOGIC: Render different views based on product type */}
        {product.product_type === 'SERVICE' ? (
          <ServiceProductView product={product} store={store} />
        ) : (
          <PhysicalProductView product={product} store={store} />
        )}

        {/* Tabs for Additional Info */}
        <div className="mt-12">
          <Tabs defaultValue="description" className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none bg-transparent p-0">
              <TabsTrigger
                value="description"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent"
              >
                Description complète
              </TabsTrigger>
              <TabsTrigger
                value="reviews"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent"
              >
                Avis clients
              </TabsTrigger>
              {store && (
                <TabsTrigger
                  value="store"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent"
                >
                  À propos du vendeur
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="description" className="mt-6">
              <Card>
                <CardContent className="p-6">
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {product.description || 'Aucune description détaillée disponible.'}
                    </p>

                    {product.specifications && (
                      <div className="mt-6">
                        <h3 className="font-semibold text-lg mb-3">Spécifications</h3>
                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(product.specifications).map(([key, value]) => (
                            <div key={key} className="flex gap-2">
                              <dt className="font-medium text-gray-700">{key}:</dt>
                              <dd className="text-gray-600">{JSON.stringify(value)}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="reviews" className="mt-6">
              <ProductReviews productId={productId} />
            </TabsContent>

            {store && (
              <TabsContent value="store" className="mt-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-6">
                      {store.logo_url ? (
                        <img
                          src={store.logo_url}
                          alt={store.name}
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center">
                          <span className="text-2xl font-bold text-gray-400">
                            {store.name[0]}
                          </span>
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{store.name}</h3>
                        <p className="text-gray-600">{store.description}</p>
                        {store.address && (
                          <p className="text-sm text-gray-500 mt-2">📍 {store.address}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                      <Link
                        to={`/boutique/${store.slug}`}
                        className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium"
                      >
                        Voir tous les produits de cette boutique
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Link>
                      {user && store.owner_id !== user.id && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5 border-orange-200 text-orange-700 hover:bg-orange-50"
                          onClick={() => {
                            sessionStorage.setItem("msg_pending_contact", JSON.stringify({ id: store.owner_id, name: store.name }));
                            navigate("/messages");
                          }}
                        >
                          <MessageCircle className="h-4 w-4" />
                          Contacter le vendeur
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Produits similaires
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.slice(0, 4).map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/produit/${relatedProduct.id}`}
                  className="group"
                >
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="aspect-square bg-gray-100">
                      <img
                        src={relatedProduct.images?.[0] || 'https://via.placeholder.com/300'}
                        alt={relatedProduct.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                    <CardContent className="p-3">
                      <h3 className="font-medium text-sm line-clamp-2 mb-1">
                        {relatedProduct.title}
                      </h3>
                      <p className="text-orange-600 font-bold">
                        {relatedProduct.price.toFixed(2)} HTG
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />

      <Dialog open={resellDialogOpen} onOpenChange={setResellDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter à votre boutique</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-gray-500">
              Ce produit sera cloné dans votre boutique. Vous pourrez ensuite modifier le prix et la description.
              Le stock est synchronisé avec le fournisseur.
            </p>
            {userStores.length > 1 && (
              <div className="space-y-2">
                <Label>Choisir la boutique</Label>
                <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {userStores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {userStores.length === 1 && (
              <p className="font-medium">Boutique : {userStores[0].name}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResellDialogOpen(false)}>Annuler</Button>
            <Button onClick={confirmRepublish} disabled={republishing || !selectedStoreId}>
              {republishing ? "Ajout..." : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
